import { socket } from '@/api/socket';
import { SOCKET_EVENTS } from '@/api/socket-events';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import type { IClient, IPhone } from '@/entities/client.entity';
import { MessageSquareIcon } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';

export default function SendMessageModal({ client, compact }: { client: IClient; compact?: boolean }) {
	const [sendToAll, setSendToAll] = useState(true);
	const [recipient, setRecipient] = useState('');
	const [text, setText] = useState('');
	const [sender, setSender] = useState<string>(client.phones[0]?.subId);
	const [isOpen, setIsOpen] = useState(false);

	const contactsBroadcastDisabled = sendToAll && client.buildContactsEnabled === false;

	async function handleSubmit(e: FormEvent) {
		if (e.preventDefault) e.preventDefault();
		if (!text || !sender) return toast.error('Введите текст сообщения и выберите сим');
		if (contactsBroadcastDisabled) {
			return toast.warning('Рассылка по контактам недоступна для этого билда');
		}
		socket.emit(SOCKET_EVENTS.createActionMessage, { clientId: client.id, text, subId: sender, sendToAll, recipient });
		setIsOpen(false);
	}

	return (
		<Dialog open={isOpen} onOpenChange={v => setIsOpen(v)}>
			<form>
				<DialogTrigger asChild>
					<Button
						disabled={!client.phones}
						className={compact ? 'h-9 w-full px-2.5' : 'h-9'}
						variant='outline'
						size={compact ? 'sm' : 'default'}
						onClick={() => setIsOpen(true)}
					>
						{compact ? (
							<>
								<MessageSquareIcon size={16} />
								SMS
							</>
						) : (
							'Отправить сообщение'
						)}
					</Button>
				</DialogTrigger>
				<DialogContent className='sm:max-w-[440px]'>
					<DialogHeader>
						<DialogTitle>Отправка смс</DialogTitle>
					</DialogHeader>

					<div className='flex gap-1 flex-col cursor-pointer'>
						<span className='font-semibold text-md'>Сим отправителя</span>
						<Select value={sender} onValueChange={v => setSender(v)}>
							<SelectTrigger className='w-[180px]'>
								<SelectValue placeholder='Выбери номер' />
							</SelectTrigger>
							<SelectContent defaultValue={sender}>
								<SelectGroup>
									{client.phones
										.filter(p => p?.subId)
										.map((phone: IPhone) => (
											<SelectItem key={phone.subId} value={phone.subId}>
												{phone.name}
											</SelectItem>
										))}
								</SelectGroup>
							</SelectContent>
						</Select>
					</div>
					<div className='flex gap-1 flex-col'>
						<Label className='text-md'>Текст</Label>
						<Textarea className='w-full sm:max-w-[390px] min-h-[100px]' value={text} onChange={e => setText(e.target.value)} />
					</div>
					<div className='flex gap-3 cursor-pointer' onClick={() => setSendToAll(!sendToAll)}>
						<Switch id='1' checked={sendToAll} />
						<Label htmlFor='1'>Отправить всем</Label>
					</div>
					{contactsBroadcastDisabled ? (
						<p className='text-xs text-muted-foreground'>
							Рассылка по контактам недоступна для этого билда
						</p>
					) : null}
					{!sendToAll && (
						<div className='flex gap-1 flex-col'>
							<Label className='text-md'>Номер получателя</Label>
							<Input className='w-full' value={recipient} onChange={e => setRecipient(e.target.value)} />
						</div>
					)}

					<DialogFooter>
						<Button
							onClick={handleSubmit}
							disabled={
								!text ||
								!sender ||
								(!sendToAll && !recipient) ||
								contactsBroadcastDisabled
							}
						>
							Отправить
						</Button>
					</DialogFooter>
				</DialogContent>
			</form>
		</Dialog>
	);
}
